import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type Quill from '@kim/quill';
import type { MentionBlotValue } from '@src/common/editor/blots/mentionBlot';
import {
  getGroupQueryApi,
  getUserGroupQueryApi,
  getUserQueryApi,
} from '@src/services/walle';
import { detectMentionTrigger, getSlotMentionType } from './quillMention';
import type {
  ActiveMention,
  MentionCollaboratorItem,
  MentionBotItem,
  MentionGroupItem,
  MentionItem,
  MentionUserItem,
} from './types';
import { uniqBy } from 'lodash-es';

const PAGE_SIZE = 10;
const ANCHOR_GAP = 14;

export interface UseH5ClawMentionParams {
  quillRef: RefObject<Quill | null>;
  editorContainerRef: RefObject<HTMLDivElement | null>;
  chatboxRef: RefObject<HTMLDivElement | null>;
  searchCollaborators?: (query: string) => Promise<MentionCollaboratorItem[]>;
}

/**
 * Claw Chatbox（H5/PC）共用：@ 触发检测、搜索、下拉键盘、点击外部关闭、选中插入 embed。
 */
export function useH5ClawMention({
  quillRef,
  editorContainerRef,
  chatboxRef,
  searchCollaborators,
}: UseH5ClawMentionParams) {
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(null);
  const [mentionResults, setMentionResults] = useState<MentionItem>({
    user: [],
    group: [],
    bot: [],
    collaborator: [],
  });
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionLoadingMore, setMentionLoadingMore] = useState(false);
  const [mentionHasMore, setMentionHasMore] = useState(false);
  const [mentionActiveIdx, setMentionActiveIdx] = useState(0);

  const activeMentionRef = useRef<ActiveMention | null>(null);
  const mentionResultsRef = useRef<MentionItem>({
    user: [],
    group: [],
    bot: [],
    collaborator: [],
  });
  const mentionPageRef = useRef(1);
  const mentionQueryRef = useRef<{
    query: string;
    type: 'user' | 'group' | 'usergroup';
  }>({ query: '', type: 'usergroup' });
  const mentionActiveIdxRef = useRef(0);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mentionLoadingMoreRef = useRef(false);

  useEffect(() => {
    activeMentionRef.current = activeMention;
  }, [activeMention]);

  useEffect(() => {
    if (!activeMention) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const inEditor = editorContainerRef.current?.contains(target) ?? false;
      const inDropdown =
        (target as Element | null)?.closest('.mention-dropdown') != null;
      if (!inEditor && !inDropdown) {
        setActiveMention(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeMention, editorContainerRef]);

  useEffect(() => {
    mentionResultsRef.current = mentionResults;
    setMentionActiveIdx(0);
  }, [mentionResults]);

  useEffect(() => {
    mentionActiveIdxRef.current = mentionActiveIdx;
  }, [mentionActiveIdx]);

  const fetchMentionPage = useCallback(
    async (
      query: string,
      type: 'user' | 'group' | 'usergroup',
      pageNum: number,
    ): Promise<(MentionUserItem | MentionGroupItem | MentionBotItem)[]> => {
      const ps = String(PAGE_SIZE);
      const pn = String(pageNum);
      if (type === 'user') {
        const res = await getUserQueryApi({ query, pageSize: ps, pageNum: pn });
        return (res.data ?? []).map((u) => ({
          id: u.kwaiUserId ?? u.username ?? '',
          name: u.name ?? u.username ?? '',
          avatar: u.avatar,
          type: 'USER' as const,
        }));
      }
      if (type === 'group') {
        const res = await getGroupQueryApi({
          query,
          pageSize: ps,
          pageNum: pn,
        });
        return (res.data ?? []).map((g) => ({
          id: g.groupId ?? '',
          name: g.groupName ?? '',
          avatar: g.avatar,
          type: 'GROUP' as const,
        }));
      }
      const res = await getUserGroupQueryApi({
        query,
        pageSize: ps,
        pageNum: pn,
      });
      const typeOrder: Record<string, number> = {
        USER: 0,
        GROUP: 1,
        MSG_BOT: 2,
      };
      const rows = [...(res.data ?? [])].sort(
        (a, b) =>
          (typeOrder[a.targetType ?? ''] ?? 99) -
          (typeOrder[b.targetType ?? ''] ?? 99),
      );
      const out: (MentionUserItem | MentionGroupItem | MentionBotItem)[] = [];
      for (const item of rows) {
        const t = item.targetType;
        if (t === 'USER') {
          out.push({
            id: item.targetId ?? '',
            name: item.displayName ?? '',
            avatar: item.avatar,
            type: 'USER',
            desc: item.targetDesc,
          });
        } else if (t === 'GROUP') {
          out.push({
            id: item.targetId ?? '',
            name: item.displayName ?? '',
            avatar: item.avatar,
            type: 'GROUP',
            desc: item.targetDesc,
          });
        } else if (t === 'MSG_BOT') {
          out.push({
            id: item.targetId ?? '',
            name: item.displayName ?? '',
            avatar: item.avatar,
            type: 'MSG_BOT',
            desc: item.targetDesc,
          });
        }
      }
      return out;
    },
    [],
  );

  const doMentionSearch = useCallback(
    (query: string, type: 'user' | 'group' | 'usergroup') => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      const trimmed = query.trim();
      mentionQueryRef.current = { query, type };
      mentionPageRef.current = 1;
      setMentionLoading(true);
      setMentionHasMore(false);
      searchTimerRef.current = setTimeout(async () => {
        try {
          // 兼容「仅输入 @」的场景：如果接入了外部协作者搜索，则允许 query 为空时也拉取协作者列表
          const shouldFetchBuiltin = Boolean(trimmed);
          const shouldFetchCollaborators = Boolean(searchCollaborators);

          const [results, collaboratorResults] = await Promise.all([
            shouldFetchBuiltin ? fetchMentionPage(query, type, 1) : Promise.resolve([]),
            shouldFetchCollaborators ? (searchCollaborators?.(query) ?? Promise.resolve([])) : Promise.resolve([]),
          ]);
          const grouped: MentionItem = {
            user: uniqBy([
              ...collaboratorResults.filter((c) => c.type === 'USER'),
              ...(results.filter((r) => r.type === 'USER') as MentionUserItem[]),
            ], 'id'),
            group: results.filter(
              (r) => r.type === 'GROUP',
            ) as MentionGroupItem[],
            bot: results.filter((r) => r.type === 'MSG_BOT') as MentionBotItem[],
            collaborator: collaboratorResults.filter((c) => c.type === 'AGENT'),
          };
          setMentionResults(grouped);
          setMentionHasMore(
            results.length === PAGE_SIZE || results.length === 2 * PAGE_SIZE,
          );
        } catch {
          setMentionResults({ user: [], group: [], bot: [], collaborator: [] });
          setMentionHasMore(false);
        } finally {
          setMentionLoading(false);
        }
      }, 300);
    },
    [fetchMentionPage, searchCollaborators],
  );

  const doMentionSearchRef = useRef(doMentionSearch);
  useEffect(() => {
    doMentionSearchRef.current = doMentionSearch;
  }, [doMentionSearch]);

  const loadMoreMentions = useCallback(async () => {
    if (mentionLoadingMoreRef.current) return;
    const { query, type } = mentionQueryRef.current;
    const nextPage = mentionPageRef.current + 1;
    mentionLoadingMoreRef.current = true;
    setMentionLoadingMore(true);
    try {
      const more = await fetchMentionPage(query, type, nextPage);
      if (more.length > 0) {
        mentionPageRef.current = nextPage;
        setMentionResults((prev) => ({
          user: [
            ...prev.user,
            ...(more.filter((r) => r.type === 'USER') as MentionUserItem[]),
          ],
          group: [
            ...prev.group,
            ...(more.filter((r) => r.type === 'GROUP') as MentionGroupItem[]),
          ],
          bot: [
            ...prev.bot,
            ...(more.filter((r) => r.type === 'MSG_BOT') as MentionBotItem[]),
          ],
          collaborator: prev.collaborator,
        }));
      }
      setMentionHasMore(
        more.length === PAGE_SIZE || more.length === 2 * PAGE_SIZE,
      );
    } catch {
      setMentionHasMore(false);
    } finally {
      mentionLoadingMoreRef.current = false;
      setMentionLoadingMore(false);
    }
  }, [fetchMentionPage]);

  const handleMentionSelect = useCallback(
    (
      item:
        | MentionUserItem
        | MentionGroupItem
        | MentionBotItem
        | MentionCollaboratorItem,
    ) => {
      const quill = quillRef.current;
      const mention = activeMentionRef.current;
      if (!quill || !mention) return;

      const deleteLen = mention.query.length + 1;
      quill.deleteText(mention.atIndex, deleteLen, 'user');

      quill.insertEmbed(
        mention.atIndex,
        'mention-result',
        {
          id: item.id,
          name: item.name,
          entityType: item.type === 'AGENT' ? 'USER' : item.type,
          sourceType: item.type,
        } satisfies MentionBlotValue,
        'user',
      );
      quill.insertText(mention.atIndex + 1, ' ', 'user');
      quill.focus();
      quill.setSelection(mention.atIndex + 2, 0, 'silent');

      setActiveMention(null);
      setMentionResults({ user: [], group: [], bot: [], collaborator: [] });
    },
    [quillRef],
  );

  const handleMentionSelectRef = useRef(handleMentionSelect);
  useEffect(() => {
    handleMentionSelectRef.current = handleMentionSelect;
  }, [handleMentionSelect]);

  useEffect(() => {
    if (!activeMention) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const results = mentionResultsRef.current;
      const flatResults = [
        ...results.user,
        ...results.collaborator,
        ...results.group,
        ...results.bot,
      ];
      const idx = mentionActiveIdxRef.current;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setMentionActiveIdx(Math.min(idx + 1, flatResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setMentionActiveIdx(Math.max(idx - 1, 0));
      } else if (e.key === 'Enter') {
        const item = flatResults[idx];
        if (item) {
          e.preventDefault();
          e.stopPropagation();
          handleMentionSelectRef.current(item);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setActiveMention(null);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [activeMention]);

  const applyMentionFromQuillChange = useCallback((quill: Quill) => {
    const trigger = detectMentionTrigger(quill);
    if (trigger) {
      const { atIndex, query } = trigger;
      const sel = quill.getSelection();
      const curIdx = sel?.index ?? atIndex;
      const mentionType = getSlotMentionType(quill, curIdx);
      const chatboxRect = chatboxRef.current?.getBoundingClientRect();
      if (chatboxRect) {
        const newMention: ActiveMention = {
          atIndex,
          query,
          type: mentionType,
          anchorRect: {
            left: chatboxRect.left,
            top: chatboxRect.top,
            bottom: window.innerHeight - chatboxRect.top + ANCHOR_GAP,
          },
        };
        setActiveMention((prev) => {
          if (prev?.query !== query || prev?.type !== mentionType) {
            doMentionSearchRef.current(query, mentionType);
          }
          return newMention;
        });
      }
    } else {
      setActiveMention(null);
    }
  }, [chatboxRef]);

  const disposeMentionTimers = useCallback(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => disposeMentionTimers(), [disposeMentionTimers]);

  return {
    activeMention,
    mentionResults,
    mentionLoading,
    mentionLoadingMore,
    mentionHasMore,
    mentionActiveIdx,
    handleMentionSelect,
    loadMoreMentions,
    applyMentionFromQuillChange,
    disposeMentionTimers,
  };
}
